/**
 * Iframe Communication Manager for Tronic SDK
 *
 * Handles bidirectional communication with the iframe app to avoid CORS issues.
 * Delegates recommendation requests to the iframe.
 */

import type {
  CommunicationManager,
  LauncherToIframeMessage,
  IframeToLauncherMessage,
  PendingRequest,
  RecommendationRequestMessage,
  RecommendationResponseMessage,
  UserDataUpdatedMessage,
} from './communication-types';

export class IframeCommunicationManager implements CommunicationManager {
  private iframe: HTMLIFrameElement | null = null;
  private isIframeReady = false;
  private pendingRequests = new Map<string, PendingRequest>();
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private requestCounter = 0;
  private readonly REQUEST_TIMEOUT = 10000; // 10 seconds
  private readonly DEBUG_ENABLED = process.env.NODE_ENV === 'development';

  constructor(iframe?: HTMLIFrameElement) {
    if (iframe) {
      this.setIframe(iframe);
    }
  }

  /**
   * Set or update the iframe reference
   */
  public setIframe(iframe: HTMLIFrameElement): void {
    if (this.iframe === iframe) {
      return; // Same iframe, no need to update
    }

    this.cleanup();
    this.iframe = iframe;
    this.setupMessageListener();
    this.checkIframeReady();
  }

  /**
   * Check if the communication manager is ready to send messages
   */
  public isReady(): boolean {
    // For cross-origin iframes, we can't access contentDocument, so we rely on contentWindow and isIframeReady
    return !!(this.iframe && this.iframe.contentWindow && (this.isIframeReady || this.iframe.src));
  }

  /**
   * Request recommendation from iframe
   */
  public async requestRecommendation(pageUrl: string, keywords: string[], context?: Record<string, any>): Promise<any> {
    if (!this.isReady()) {
      this.debugLog('Recommendation request skipped - iframe not ready');
      return null;
    }

    try {
      const requestId = this.generateRequestId();
      const message: RecommendationRequestMessage = {
        type: 'GET_RECOMMENDATION',
        payload: {
          requestId,
          data: {
            page_url: pageUrl,
            keywords,
            context: {
              ...context,
              sdk_version: 'tronic-sdk',
              sent_from: 'launcher',
            },
          },
        },
      };

      const response = await this.sendMessageWithResponse(
        message,
        (response): response is RecommendationResponseMessage => {
          return (
            response.type === 'RECOMMENDATION_RESPONSE' &&
            'requestId' in response.payload &&
            response.payload.requestId === requestId
          );
        },
      );

      if (response && response.payload.success) {
        return response.payload.data;
      }
      const error = response?.payload?.error;
      const errorMessage = typeof error === 'string' ? error : error?.message;
      console.warn('❌ [IframeCommunication] Recommendation request failed:', errorMessage);
      return null;
    } catch (error) {
      console.error('[IframeCommunication] Failed to request recommendation:', error);
      return null;
    }
  }

  /**
   * Send message to iframe and wait for response
   */
  private async sendMessageWithResponse<T extends IframeToLauncherMessage>(
    message: LauncherToIframeMessage,
    responseFilter: (response: IframeToLauncherMessage) => response is T,
    timeoutMs: number = this.REQUEST_TIMEOUT,
  ): Promise<T | null> {
    if (!this.iframe?.contentWindow) {
      throw new Error('Iframe not available');
    }

    const requestId = 'requestId' in message.payload ? message.payload.requestId : this.generateRequestId();

    return new Promise<T | null>((resolve, reject) => {
      // Set up timeout
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error('Request timeout'));
      }, timeoutMs);

      // Store request
      const pendingRequest: PendingRequest = {
        requestId,
        resolve: (data: T) => {
          clearTimeout(timeout);
          resolve(data);
        },
        reject: (error: Error) => {
          clearTimeout(timeout);
          reject(error);
        },
        timeout,
        timestamp: Date.now(),
      };

      this.pendingRequests.set(requestId, pendingRequest);

      // Send message
      try {
        this.iframe!.contentWindow!.postMessage(message, '*');
        this.debugLog('Message sent to iframe:', message.type, message);
      } catch (error) {
        clearTimeout(timeout);
        this.pendingRequests.delete(requestId);
        reject(error);
      }
    }).catch((error) => {
      // Handle timeout gracefully for non-critical requests
      if (error.message === 'Request timeout') {
        this.debugLog('Request timed out:', message.type);
        return null;
      }
      throw error;
    });
  }

  /**
   * Setup message listener for iframe responses
   */
  private setupMessageListener(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }

    this.messageListener = (event: MessageEvent<IframeToLauncherMessage>) => {
      // Security check - only accept messages from trusted origins
      if (!this.isMessageFromTrustedOrigin(event.origin)) {
        return;
      }

      const message = event.data;
      if (!message || !message.type) {
        return;
      }

      this.debugLog('Message received from iframe:', message.type);

      // Handle different message types
      switch (message.type) {
        case 'RECOMMENDATION_RESPONSE':
          this.handleRecommendationResponse(message);
          break;

        case 'TRONIC_USER_DATA_UPDATED':
          this.handleUserDataUpdated(message);
          break;

        default:
          this.debugLog('Unknown message type received:', (message as any).type);
      }
    };

    window.addEventListener('message', this.messageListener);
  }

  /**
   * Handle recommendation response
   */
  private handleRecommendationResponse(message: RecommendationResponseMessage): void {
    const { requestId } = message.payload;
    const pendingRequest = this.pendingRequests.get(requestId);

    if (pendingRequest) {
      pendingRequest.resolve(message);
      this.pendingRequests.delete(requestId);
    } else {
      this.debugLog('Received response for unknown request ID:', requestId);
    }
  }

  /**
   * Handle user data updated message from iframe
   */
  private handleUserDataUpdated(message: UserDataUpdatedMessage): void {
    this.debugLog('User data updated from iframe:', message.payload.user?.user_id);

    // This message is informational - the iframe is notifying that user data has changed
    // We don't need to do anything specific with this message currently
    // but handling it prevents "unknown message type" warnings
  }

  /**
   * Check if iframe is ready to receive messages
   */
  private checkIframeReady(): void {
    if (!this.iframe) {
      return;
    }

    const checkReady = () => {
      // For cross-origin iframes, we can't access contentDocument
      // So we assume the iframe is ready after the load event
      setTimeout(() => {
        this.isIframeReady = true;
        this.debugLog('Iframe is ready for communication');
      }, 500); // Give iframe more time to initialize
    };

    // Always use load event for cross-origin iframe
    this.iframe.addEventListener('load', checkReady, { once: true });

    // Also try immediately if iframe seems to be loaded already
    if (this.iframe.src && this.iframe.contentWindow) {
      setTimeout(checkReady, 1000);
    }
  }

  /**
   * Check if message origin is trusted
   */
  private isMessageFromTrustedOrigin(origin: string): boolean {
    const trustedDomains = ['tronic.app', 'localhost', '127.0.0.1'];
    return trustedDomains.some((domain) => origin.includes(domain));
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    this.requestCounter = (this.requestCounter + 1) % 10000;
    return `req_${Date.now()}_${this.requestCounter}`;
  }

  /**
   * Debug logging
   */
  private debugLog(message: string, ...args: any[]): void {
    if (this.DEBUG_ENABLED) {
      console.debug(`[IframeCommunication] ${message}`, ...args);
    }
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    // Clear message listener
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }

    // Clear pending requests
    this.pendingRequests.forEach((request) => {
      clearTimeout(request.timeout);
      request.reject(new Error('Communication manager destroyed'));
    });
    this.pendingRequests.clear();

    // Reset state
    this.isIframeReady = false;
  }

  /**
   * Destroy the communication manager
   */
  public destroy(): void {
    this.cleanup();
    this.iframe = null;
  }
}

// Singleton instance for global access
let globalCommunicationManager: IframeCommunicationManager | null = null;

/**
 * Get or create the global communication manager instance
 */
export function getCommunicationManager(iframe?: HTMLIFrameElement): IframeCommunicationManager {
  if (!globalCommunicationManager) {
    globalCommunicationManager = new IframeCommunicationManager(iframe);
  } else if (iframe) {
    globalCommunicationManager.setIframe(iframe);
  }

  return globalCommunicationManager;
}
